import { z } from 'zod';
import { emailSchema, isoDay, objectIdSchema } from './common.js';
import { companyNumberInputSchema } from './company-number.js';
import { portalLocaleSchema } from './locale.js';
/**
 * A signed partner convention. One convention can cover several partners (IBGraf and IBLux signed
 * one together), each with its own environment. Commission rules (art. 3, 5.3) hang off the
 * convention: the rate per period, start/end, and why it ended (the tail only applies when the
 * partner was not at fault).
 */
export const conventionEndReasonSchema = z.enum(['notice', 'mutual', 'partner_fault']);
export const commissionRateSchema = z.object({
    /** 0.2 = 20 %. */
    rate: z.number().min(0).max(1),
    /** First day the rate applies (Brussels). */
    from: isoDay,
});
export const conventionInputSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    signedOn: isoDay,
    startsOn: isoDay,
    commissionRates: z.array(commissionRateSchema).min(1).max(20),
    tailMonths: z.number().int().min(0).max(24).default(6),
})
    .strict();
export const endConventionSchema = z
    .object({ endsOn: isoDay, endReason: conventionEndReasonSchema })
    .strict();
/** active: can log in; suspended: art. 6.2, no access, nothing accrues for payout; ended: art. 5.4. */
export const partnerStatusSchema = z.enum(['active', 'suspended', 'ended']);
export const partnerInputSchema = z
    .object({
    conventionId: objectIdSchema,
    /** Also the option value of the HubSpot `partner` property. */
    slug: z
        .string()
        .trim()
        .regex(/^[a-z0-9][a-z0-9-]{1,39}$/, 'lowercase letters, digits and dashes'),
    legalName: z.string().trim().min(1).max(200),
    companyNumber: companyNumberInputSchema,
    defaultLocale: portalLocaleSchema.default('fr'),
    /** The partner sells to its leads itself and reports their status in the portal. */
    salesByPartner: z.boolean().default(false),
})
    .strict();
export const updatePartnerSchema = z
    .object({
    legalName: z.string().trim().min(1).max(200).optional(),
    defaultLocale: portalLocaleSchema.optional(),
    salesByPartner: z.boolean().optional(),
    status: partnerStatusSchema.optional(),
})
    .strict();
export const partnerUserStatusSchema = z.enum(['invited', 'active', 'disabled']);
export const invitePartnerUserSchema = z
    .object({
    email: emailSchema,
    name: z.string().trim().min(1).max(120),
    locale: portalLocaleSchema.optional(),
})
    .strict();
export const updatePartnerUserSchema = z
    .object({
    name: z.string().trim().min(1).max(120).optional(),
    locale: portalLocaleSchema.optional(),
    status: z.enum(['active', 'disabled']).optional(),
})
    .strict();
